import AbstractShape, { Dimensions, DimensionProperties } from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import DimensionLine from "@/Models/Lines/DimensionLine";
import GuideLine from "@/Models/Lines/GuideLine";
import { makerjs } from "../../../../source/js/shared/rendering/makerjs";
import { isEmpty } from "lodash";

export class Kite extends AbstractShape {

	constructor( dimensions?: object ) {
		super( 'kite', dimensions );
	}

	getHeight(): Length {
		return this.getHeightTop().add( this.getHeightBottom() );
	}

	getLength(): Length {
		return this.getWidth();
	}

	getWidth(): Length {
		return this.cleanedPrimitives.width;
	}

	getHeightTop(): Length {
		return this.cleanedPrimitives.height_top;
	}

	getHeightBottom(): Length {
		return this.cleanedPrimitives.height_bottom;
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'width', secondary: 'height_top', tertiary: 'height_bottom' };
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		let dimensionLines = [];
		if(focusedElement.includes("cutout_measurement") || forceRender) {
			dimensionLines = [
				new DimensionLine(
					[0, 0],
					[0, this.getHeight().in_mm()],
					this.renderDimensionLabel('height', this.getHeight().in_cm()),
					[-40, 0],
					ratio
				),
				new DimensionLine(
					[0, 0],
					[this.getWidth().in_mm(), 0],
					this.renderDimensionLabel('width', this.getWidth().in_cm()),
					[0, -40],
					ratio
				),
			];
		}

		if(focusedElement.includes("measurements-kite-width")) {
			dimensionLines.push(
				new DimensionLine(
					[0, this.getHeightBottom().in_mm()],
					[this.getWidth().in_mm(), this.getHeightBottom().in_mm()],
					this.renderDimensionLabel('width', this.getWidth().in_cm()),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-kite-width', focusedElement, forceRender)
				)
			);
		}

		if(focusedElement.includes("measurements-kite-height_top")) {
			dimensionLines.push(
				new DimensionLine(
					[this.getWidth().divide(2).in_mm(), this.getHeightBottom().in_mm()],
					[this.getWidth().divide(2).in_mm(), this.getHeight().in_mm()],
					this.renderDimensionLabel('height_top', this.getHeightTop().in_cm()),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-kite-height_top', focusedElement, forceRender)
				)
			);
		}

		if(focusedElement.includes("measurements-kite-height_bottom")) {
			dimensionLines.push(
				new DimensionLine(
					[this.getWidth().divide(2).in_mm(), 0],
					[this.getWidth().divide(2).in_mm(), this.getHeightBottom().in_mm()],
					this.renderDimensionLabel('height_bottom', this.getHeightBottom().in_cm()),
					[0, 0],
					ratio,
					this.getDimensionLineType('measurements-kite-height_bottom', focusedElement, forceRender)
				)
			);
		}

		return dimensionLines;
	}

	getGuideLines(focusedElement = ''): GuideLine[] {
		if(focusedElement.includes("measurements-kite-height_bottom") || focusedElement.includes("measurements-kite-height_top")) {
			return [
				new GuideLine(
					[0, this.getHeightBottom().in_mm()],
					[this.getWidth().in_mm(), this.getHeightBottom().in_mm()]
				),
			];
		}

		return [];
	}

	createModel(): void {
		if(isEmpty(this.cleanedPrimitives)) {
			return;
		}

		super.createModel();
		makerjs.model.zero(this.model);
		makerjs.model.originate(this.model);
	}

	getDefaultDimensions(): Dimensions {
		return {
			width: 50,
			height_top: 30,
			height_bottom: 50,
		};
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const height = enclosingRectangle.height();
		const width = enclosingRectangle.width();

		const height_top = height.divide(4).in_cm();
		const height_bottom = height_top + ( height_top / 2 );

		return {
			width: width.divide(2).in_cm(),
			height_top: height_top,
			height_bottom: height_bottom,
		};
	}
}
